import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { listViews, saveView, deleteView, togglePinned } from "../services/savedViewsService";

export default function SavedViewsPage() {
  const {
    collectionName,
    docIdQuery,
    fieldQuery,
    sortField,
    sortAsc,
    recordLimit,
    selectCollection,
    setDocIdQuery,
    setFieldQuery,
    setSortField,
    setSortAsc,
    setRecordLimit,
  } = useFirestoreAdmin();
  const navigate = useNavigate();

  const [views, setViews] = useState(() => listViews());
  const [name, setName] = useState("");

  const refresh = () => setViews(listViews());

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Give the view a name");
      return;
    }
    if (!collectionName) {
      toast.error("Select a collection in the Explorer first");
      return;
    }
    saveView({ name: name.trim(), collection: collectionName, docIdQuery, fieldQuery, sortField, sortAsc, recordLimit });
    setName("");
    refresh();
    toast.success("View saved");
  };

  const applyView = (view) => {
    selectCollection(view.collection);
    setDocIdQuery(view.docIdQuery || "");
    setFieldQuery(view.fieldQuery || "");
    setSortField(view.sortField || "id");
    setSortAsc(view.sortAsc !== false);
    setRecordLimit(view.recordLimit || 100);
    toast.success(`Applied "${view.name}"`);
    navigate("../explorer");
  };

  const handleDelete = (id) => {
    deleteView(id);
    refresh();
  };

  const handlePin = (id) => {
    togglePinned(id);
    refresh();
  };

  return (
    <div className="space-y-4">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Productivity</p>
        <h1 className="text-2xl font-bold">Saved Views</h1>
        <p className="text-sm text-slate-600">Bookmark collection + filter + sort presets and re-apply them instantly.</p>
      </header>

      <div className="fa-card space-y-3 p-4">
        <h2 className="font-semibold">Save current Explorer state</h2>
        <div className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div><span className="text-slate-500">Collection:</span> <span className="font-mono">{collectionName || "—"}</span></div>
          <div><span className="text-slate-500">Doc ID filter:</span> <span className="font-mono">{docIdQuery || "—"}</span></div>
          <div><span className="text-slate-500">Field search:</span> <span className="font-mono">{fieldQuery || "—"}</span></div>
          <div><span className="text-slate-500">Sort:</span> <span className="font-mono">{sortField} {sortAsc ? "↑" : "↓"}</span></div>
          <div><span className="text-slate-500">Limit:</span> <span className="font-mono">{recordLimit}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            placeholder="View name (e.g. Active users, this month)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <button type="button" className="fa-btn fa-btn-primary" onClick={handleSave}>Save view</button>
        </div>
      </div>

      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Collection</th>
              <th>Filters</th>
              <th>Sort</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {views.map((v) => (
              <tr key={v.id}>
                <td>
                  <button type="button" className="mr-1" onClick={() => handlePin(v.id)} title={v.pinned ? "Unpin" : "Pin"}>
                    {v.pinned ? "★" : "☆"}
                  </button>
                  {v.name}
                </td>
                <td className="font-mono text-xs">{v.collection}</td>
                <td className="text-xs text-slate-500">
                  {[v.docIdQuery && `id:${v.docIdQuery}`, v.fieldQuery && `search:${v.fieldQuery}`].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="font-mono text-xs">{v.sortField} {v.sortAsc ? "↑" : "↓"} · {v.recordLimit}</td>
                <td>
                  <div className="flex gap-2">
                    <button type="button" className="fa-btn fa-btn-primary" onClick={() => applyView(v)}>Apply</button>
                    <button type="button" className="fa-btn fa-btn-danger" onClick={() => handleDelete(v.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {!views.length && (
              <tr>
                <td colSpan={5} className="text-center text-sm text-slate-500">
                  No saved views yet. Configure the Explorer, then save it here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
